import * as Yup from 'yup';
import { Box, Stack } from '@mui/material';
import { Field, FieldProps, Form, Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { PoolButton, PoolTextField, PoolTypography } from '@synergy/components';
import {
    getInternalSettings,
    internalSettingsSelector,
    saveInternalSettings,
    useAppDispatch,
} from '@synergy/admin/app-settings/data-access';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

type InternalSettingsFormValues = {
    lobsterXmlOrderUrl: string;
    defaultCfpTextContract: string;
    defaultCfpTextBonus: string;
};

export const LobsterXml = () => {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const { lobsterUrl, defaultCfpTextContract, defaultCfpTextBonus, statusScale, isRequesting } =
        useSelector(internalSettingsSelector);

    useEffect(() => {
        dispatch(getInternalSettings());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = (values: InternalSettingsFormValues) => {
        dispatch(
            saveInternalSettings({
                lobsterXmlOrderUrl: values.lobsterXmlOrderUrl,
                defaultCfpTextContract: values.defaultCfpTextContract,
                defaultCfpTextBonus: values.defaultCfpTextBonus,
                // Sent along unchanged: the endpoint writes the whole set, so
                // leaving it out here would wipe the status scale.
                statusScale,
            })
        );
    };

    const initialValues: InternalSettingsFormValues = {
        lobsterXmlOrderUrl: lobsterUrl,
        defaultCfpTextContract: defaultCfpTextContract,
        defaultCfpTextBonus: defaultCfpTextBonus,
    };

    const InternalSettingsSchema = Yup.object().shape({
        lobsterXmlOrderUrl: Yup.string().url('app.error.url'),
        defaultCfpTextContract: Yup.string(),
        defaultCfpTextBonus: Yup.string(),
    });

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={InternalSettingsSchema}
            enableReinitialize={true}
        >
            <Form noValidate={true}>
                <Stack gap={1.5} sx={{ width: '100%' }}>
                    <PoolTypography
                        component="span"
                        typography="body1"
                        id="app.settings.system.internalSettings"
                        sx={{ fontWeight: 600 }}
                    />

                    <Stack gap={3}>
                        <Field name="lobsterXmlOrderUrl">
                            {({ field, meta }: FieldProps<string>) => (
                                <PoolTextField
                                    {...field}
                                    label={<FormattedMessage id="app.settings.system.lobsterUrl" />}
                                    placeholder={intl.formatMessage({ id: 'app.common.exampleLink' })}
                                    fullWidth={true}
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                />
                            )}
                        </Field>

                        <Field name="defaultCfpTextContract">
                            {({ field, meta }: FieldProps<string>) => (
                                <PoolTextField
                                    {...field}
                                    label={<FormattedMessage id="app.settings.system.defaultCfpTextContract" />}
                                    fullWidth={true}
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                />
                            )}
                        </Field>

                        <Field name="defaultCfpTextBonus">
                            {({ field, meta }: FieldProps<string>) => (
                                <PoolTextField
                                    {...field}
                                    label={<FormattedMessage id="app.settings.system.defaultCfpTextBonus" />}
                                    fullWidth={true}
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                />
                            )}
                        </Field>

                        <Box>
                            <PoolButton
                                styleType="firstLevel"
                                variant="contained"
                                disabled={isRequesting}
                                type="submit"
                                sx={{ minWidth: 162 }}
                            >
                                <FormattedMessage id="app.common.save" />
                            </PoolButton>
                        </Box>
                    </Stack>
                </Stack>
            </Form>
        </Formik>
    );
};
